import { Injectable } from '@angular/core';
import { ProductOPReq, ProductService, ProductsOpenPlatformService, ProductsResponseOP } from '@cemex-core/cmx-api';
import { TopProductsReq } from '@cemex-core/cmx-api/models/product/top-products-req';
import { TopProductsResp } from '@cemex-core/cmx-api/models/product/top-products-resp';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { SALES_DOCUMENT_TYPE_ID } from '../../../../shared/constants/sales-document-type-id';
import { getOrderBuilderState, OrderBuilderFeatureState, OrderBuilderState } from '../../../reducers';
import { getAnticipatedInvoice } from '../../review/reducers/review.selectors';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import {
  ProductsActionTypes,
  ProductsLoadAction,
  ProductsLoadCompleteAction,
  ProductsULinkLoadAction,
  ProductsULinkLoadCompleteAction,
} from '../actions/products.actions';
import {
  RetailSpecificationActionTypes,
  RetailSpecificationLoadAction,
} from '../actions/retail-specifications.actions';
import { FieldButtonModalConfig } from '../models/form-config/field-button-modal-config';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';

@Injectable()
export class TopProductsEffects {
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RetailSpecificationActionTypes.INIT),
        tap((action: RetailSpecificationLoadAction) => {
          const field = action.payload.proto.find(
            (item) => item.name === SpecFieldNames.CATALOG,
          ) as FieldButtonModalConfig;
          this.store.dispatch(new ProductsLoadAction(field.dropdownSalesDocumentTypes));
        }),
      ),
    { dispatch: false },
  );

  topProductsLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductsActionTypes.LOAD),
        // Retrieve anticipated invoice state
        switchMap((action) =>
          this.store.select(getAnticipatedInvoice).pipe(
            map((invoice) => ({ action, invoice })),
            first(),
          ),
        ),
        // Continue if there is no invoice only
        filter(({ invoice }) => !invoice),
        map(({ action }) => action),
        switchMap((action: ProductsLoadAction) => {
          return this.store.select(getOrderBuilderState).pipe(
            take(1),
            filter((state) => !this.hasTopProductsLoaded(state)), // Continue if there are no products loaded
            map((state: OrderBuilderFeatureState) => {
              return action.payload.map((salesDocumentTypeId) => {
                return this.makeTopProductsRequestFromState(state, salesDocumentTypeId);
              });
            }),
            switchMap((requests: TopProductsReq[]) => {
              return forkJoin(
                requests.map((request) => {
                  return this.productService.getTopProducts(request);
                }),
              );
            }),
          );
        }),
        tap((results: TopProductsResp[]) => {
          const products = results.reduce((acc, response) => {
            return [...acc, ...response.products];
          }, []);
          this.store.dispatch(new ProductsLoadCompleteAction(products));
        }),
      ),
    { dispatch: false },
  );

  /**
   * When coming back from review step, this effects gets executed again
   * But if the products are already in store then it's not needed to make the api call again
   *
   * @returns true if there are already products loaded
   * @param state
   */
  private hasTopProductsLoaded(state: OrderBuilderFeatureState): boolean {
    return !!state.retailSpecificationPage.productsState.availableProducts.length;
  }

  /**
   *
   * @param {OrderBuilderFeatureState} state
   * @param {SALES_DOCUMENT_TYPE_ID} salesDocumentTypeId
   * @return {TopProductsReq}
   */
  public makeTopProductsRequestFromState(
    state: OrderBuilderFeatureState,
    salesDocumentTypeId: SALES_DOCUMENT_TYPE_ID,
  ): TopProductsReq {
    return {
      shipmentLocationId: state.locationPage.jobsite.selected.shipmentLocationId,
      shippingConditionId: state.businessLinePage.shippingCondition.selected.shippingConditionId,
      productLineId: state.businessLinePage.businessLine.selected.productTypeId,
      salesDocumentTypeId,
    } as TopProductsReq;
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private productService: ProductService,
  ) {}
}

/**
 * Used for the transition of products order API
 * Should allow to remove TopProductsEffects as legacy
 * when a country adopts 'products' and ODM creation APIs
 * (country toggle included)
 */
@Injectable()
export class TopProductsULinkEffects {
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RetailSpecificationActionTypes.INIT),
        tap((action: RetailSpecificationLoadAction) => {
          const field = action.payload.proto.find(
            (item) => item.name === SpecFieldNames.CATALOG,
          ) as FieldButtonModalConfig;
          const enableODM = this.featureToggleService.isFeatureEnabled(Features.EnableOdmOrderCreation);
          if (enableODM) {
            this.store.dispatch(new ProductsULinkLoadAction(field.dropdownSalesDocumentTypes));
          } else {
            this.store.dispatch(new ProductsLoadAction(field.dropdownSalesDocumentTypes));
          }
        }),
      ),
    { dispatch: false },
  );

  topProductsLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductsActionTypes.LOAD_ULINK),
        // Retrieve anticipated invoice state
        switchMap((action) =>
          this.store.select(getAnticipatedInvoice).pipe(
            map((invoice) => ({ action, invoice })),
            first(),
          ),
        ),
        // Continue if there is no invoice only
        filter(({ invoice }) => !invoice),
        map(({ action }) => action),
        switchMap((action: ProductsULinkLoadAction) => {
          return this.store.select(getOrderBuilderState).pipe(
            take(1),
            filter((state) => !this.hasULinkTopProductsLoaded(state)),
            map((state: OrderBuilderFeatureState) => {
              return this.makeTopProductsRequestULink(state);
            }),
            switchMap((request: ProductOPReq) => {
              return this.productApi.getProducts(request);
            }),
          );
        }),
        tap((results: ProductsResponseOP) => {
          this.store.dispatch(new ProductsULinkLoadCompleteAction(results));
        }),
      ),
    { dispatch: false },
  );

  /**
   * Used to cath action for legacy request in case toggle for feature is disabled
   */

  topProductsLegacyLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductsActionTypes.LOAD),
        // Retrieve anticipated invoice state
        switchMap((action) =>
          this.store.select(getAnticipatedInvoice).pipe(
            map((invoice) => ({ action, invoice })),
            first(),
          ),
        ),
        // Continue if there is no invoice only
        filter(({ invoice }) => !invoice),
        map(({ action }) => action),
        switchMap((action: ProductsLoadAction) => {
          return this.store.select(getOrderBuilderState).pipe(
            take(1),
            filter((state) => !this.hasULinkTopProductsLoaded(state)), // Continue if there are no products loaded
            map((state: OrderBuilderFeatureState) => {
              return action.payload.map((salesDocumentTypeId) => {
                return this.makeTopProductsRequestFromState(state, salesDocumentTypeId);
              });
            }),
            switchMap((requests: TopProductsReq[]) => {
              return forkJoin(
                requests.map((request) => {
                  return this.productService.getTopProducts(request);
                }),
              );
            }),
          );
        }),
        tap((results: TopProductsResp[]) => {
          const products = results.reduce((acc, response) => {
            return [...acc, ...response.products];
          }, []);
          this.store.dispatch(new ProductsLoadCompleteAction(products));
        }),
      ),
    { dispatch: false },
  );

  /**
   * When coming back from review step, this effects gets executed again
   * But if the products are already in store then it's not needed to make the api call again
   *
   * @returns true if there are already products loaded
   * @param state
   */
  private hasULinkTopProductsLoaded(state: OrderBuilderFeatureState): boolean {
    const enableODM = this.featureToggleService.isFeatureEnabled(Features.EnableOdmOrderCreation);
    return enableODM
      ? !!state.retailSpecificationPage.productsState.availableProductsULink.length
      : !!state.retailSpecificationPage.productsState.availableProducts.length;
  }

  private makeTopProductsRequestULink(state: OrderBuilderFeatureState): ProductOPReq {
    return {
      productTypeIds: state.businessLinePage.businessLine.selected.productTypeId.toString(),
      jobsiteId: state.locationPage.jobsite.selected.shipmentLocationId,
      fetch: 10000,
      subGroup: '0',
    };
  }

  /**
   *
   * @param {OrderBuilderFeatureState} state
   * @param {SALES_DOCUMENT_TYPE_ID} salesDocumentTypeId
   * @return {TopProductsReq}
   */
  public makeTopProductsRequestFromState(
    state: OrderBuilderFeatureState,
    salesDocumentTypeId: SALES_DOCUMENT_TYPE_ID,
  ): TopProductsReq {
    return {
      shipmentLocationId: state.locationPage.jobsite.selected.shipmentLocationId,
      shippingConditionId: state.businessLinePage.shippingCondition.selected.shippingConditionId,
      productLineId: state.businessLinePage.businessLine.selected.productTypeId,
      salesDocumentTypeId,
    } as TopProductsReq;
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private productApi: ProductsOpenPlatformService,
    private featureToggleService: FeatureToggleService,
    private productService: ProductService,
  ) {}
}
